// src/pages/training-videos-library/components/CategoryManagerModal.jsx
// Admin-only CRUD panel for video_categories.
// Create, rename, change icon, toggle active, delete (with safety guard).

import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

// All available icons the admin can pick
const ICON_OPTIONS = [
  { name: 'Stethoscope', label: 'Procédures'     },
  { name: 'Wrench',      label: 'Équipement'     },
  { name: 'Shield',      label: 'Sécurité'       },
  { name: 'Monitor',     label: 'Logiciel'       },
  { name: 'Activity',    label: 'Clinique'       },
  { name: 'FileText',    label: 'Administration' },
  { name: 'BookOpen',    label: 'Formation'      },
  { name: 'Microscope',  label: 'Laboratoire'    },
  { name: 'Heart',       label: 'Santé'          },
  { name: 'Users',       label: 'Équipe'         },
  { name: 'Settings',    label: 'Système'        },
  { name: 'Star',        label: 'Avancé'         },
  { name: 'Folder',      label: 'Général'        },
  { name: 'Play',        label: 'Vidéo'          },
  { name: 'Award',       label: 'Certification'  },
];

// Slugify: "My Category Name" → "my-category-name"
function toSlug(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

// ── Inline edit row ────────────────────────────────────────────
const CategoryRow = ({ cat, onSave, onDelete, isSaving, isDeleting }) => {
  const [editing, setEditing]     = useState(false);
  const [name, setName]           = useState(cat.name);
  const [icon, setIcon]           = useState(cat.icon_name || 'Folder');
  const [showIcons, setShowIcons] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const nameRef = useRef(null);

  const startEdit = () => {
    setEditing(true);
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  const cancelEdit = () => {
    setEditing(false);
    setName(cat.name);
    setIcon(cat.icon_name || 'Folder');
    setShowIcons(false);
  };

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ ...cat, name: name.trim(), icon_name: icon });
    setEditing(false);
    setShowIcons(false);
  };

  return (
    <div className={`group relative rounded-xl border transition-all duration-200 ${
      editing
        ? 'border-primary/40 bg-primary/5 shadow-sm'
        : 'border-border bg-card hover:border-primary/20 hover:shadow-sm'
    }`}>

      {/* Main row */}
      <div className="flex items-center gap-3 p-3">

        {/* Icon picker trigger */}
        <div className="relative">
          <button
            disabled={!editing}
            onClick={() => editing && setShowIcons((p) => !p)}
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
              editing
                ? 'bg-primary/10 hover:bg-primary/20 cursor-pointer ring-2 ring-primary/20'
                : 'bg-muted cursor-default'
            }`}
          >
            <Icon name={icon} size={18} color={editing ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
          </button>

          {/* Icon grid dropdown */}
          {showIcons && (
            <div className="absolute left-0 top-12 z-50 w-56 p-2 bg-card border border-border rounded-xl shadow-xl grid grid-cols-5 gap-1">
              {ICON_OPTIONS.map((opt) => (
                <button
                  key={opt.name}
                  title={opt.label}
                  onClick={() => { setIcon(opt.name); setShowIcons(false); }}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:bg-muted ${
                    icon === opt.name ? 'bg-primary/10 ring-2 ring-primary/30' : ''
                  }`}
                >
                  <Icon name={opt.name} size={16} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Name field */}
        {editing ? (
          <input
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') cancelEdit(); }}
            className="flex-1 bg-background border border-primary/30 rounded-lg px-3 py-1.5 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        ) : (
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-foreground">{cat.name}</span>
            <p className="text-xs text-muted-foreground font-mono mt-0.5">{cat.slug}</p>
          </div>
        )}

        {/* Video count badge */}
        {!editing && (
          <span className="px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground rounded-full">
            {cat.video_count ?? 0} vidéo{(cat.video_count ?? 0) !== 1 ? 's' : ''}
          </span>
        )}

        {/* Active toggle */}
        <button
          onClick={() => onSave({ ...cat, is_active: !cat.is_active })}
          title={cat.is_active ? 'Désactiver' : 'Activer'}
          className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0 ${
            cat.is_active ? 'bg-success' : 'bg-muted'
          }`}
        >
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
            cat.is_active ? 'left-4' : 'left-0.5'
          }`} />
        </button>

        {/* Action buttons */}
        {editing ? (
          <div className="flex items-center gap-1">
            <button
              onClick={handleSave}
              disabled={!name.trim() || isSaving}
              className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Icon name="Check" size={14} />
              )}
            </button>
            <button
              onClick={cancelEdit}
              className="w-8 h-8 rounded-lg bg-muted text-muted-foreground flex items-center justify-center hover:bg-muted/80 transition-colors"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={startEdit}
              className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              title="Modifier"
            >
              <Icon name="Pencil" size={14} />
            </button>
            <button
              onClick={() => setConfirmDel(true)}
              className="w-8 h-8 rounded-lg hover:bg-error/10 flex items-center justify-center text-muted-foreground hover:text-error transition-colors"
              title="Supprimer"
            >
              <Icon name="Trash2" size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {confirmDel && (
        <div className="mx-3 mb-3 p-3 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-sm text-error font-medium mb-1">Supprimer « {cat.name} » ?</p>
          <p className="text-xs text-muted-foreground mb-3">
            {(cat.video_count ?? 0) > 0
              ? `⚠️ ${cat.video_count} vidéo(s) utilisent cette catégorie. Elles ne seront pas supprimées mais n'auront plus de catégorie.`
              : 'Cette action est irréversible.'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => { onDelete(cat.id); setConfirmDel(false); }}
              disabled={isDeleting}
              className="flex-1 py-1.5 bg-error text-white text-xs font-medium rounded-lg hover:bg-error/90 disabled:opacity-50 transition-colors"
            >
              {isDeleting ? 'Suppression...' : 'Confirmer'}
            </button>
            <button
              onClick={() => setConfirmDel(false)}
              className="flex-1 py-1.5 bg-muted text-foreground text-xs font-medium rounded-lg hover:bg-muted/80 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Modal ─────────────────────────────────────────────────
const CategoryManagerModal = ({ onClose, onCategoriesChanged }) => {
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [savingId, setSavingId]       = useState(null);
  const [deletingId, setDeletingId]   = useState(null);
  const [toast, setToast]             = useState(null);

  // New category form
  const [newName, setNewName]         = useState('');
  const [newIcon, setNewIcon]         = useState('Folder');
  const [showNewIcons, setShowNewIcons] = useState(false);
  const [creating, setCreating]       = useState(false);
  const [createError, setCreateError] = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Load categories with video count
  const loadCategories = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('video_categories')
      .select('id, slug, name, icon_name, is_active, training_videos(id)')
      .order('name');

    if (!error && data) {
      setCategories(
        data.map((c) => ({
          ...c,
          video_count: Array.isArray(c.training_videos) ? c.training_videos.length : 0,
        }))
      );
    }
    setLoading(false);
  };

  useEffect(() => { loadCategories(); }, []);

  // Close on Escape
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  // ── Save (update) ──
  const handleSave = async (cat) => {
    setSavingId(cat.id);
    const { error } = await supabase
      .from('video_categories')
      .update({
        name:      cat.name,
        icon_name: cat.icon_name,
        is_active: cat.is_active,
        slug:      toSlug(cat.name),
      })
      .eq('id', cat.id);

    setSavingId(null);
    if (error) {
      showToast('Erreur: ' + error.message, 'error');
    } else {
      showToast('Catégorie mise à jour');
      loadCategories();
      onCategoriesChanged?.();
    }
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    setDeletingId(id);
    const { error } = await supabase
      .from('video_categories')
      .delete()
      .eq('id', id);

    setDeletingId(null);
    if (error) {
      showToast('Erreur: ' + error.message, 'error');
    } else {
      showToast('Catégorie supprimée');
      loadCategories();
      onCategoriesChanged?.();
    }
  };

  // ── Create ──
  const handleCreate = async () => {
    if (!newName.trim()) { setCreateError('Le nom est requis'); return; }
    const slug = toSlug(newName.trim());
    if (!slug) { setCreateError('Nom invalide'); return; }

    setCreating(true);
    setCreateError('');

    const { error } = await supabase
      .from('video_categories')
      .insert({ name: newName.trim(), slug, icon_name: newIcon, is_active: true });

    setCreating(false);
    if (error) {
      if (error.code === '23505') {
        setCreateError('Cette catégorie existe déjà');
      } else {
        setCreateError('Erreur: ' + error.message);
      }
    } else {
      setNewName('');
      setNewIcon('Folder');
      showToast('Catégorie créée avec succès');
      loadCategories();
      onCategoriesChanged?.();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="LayoutGrid" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Gérer les catégories</h2>
              <p className="text-xs text-muted-foreground">{categories.length} catégorie{categories.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* ── Create new ── */}
        <div className="px-6 py-4 border-b border-border bg-muted/20 flex-shrink-0">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Nouvelle catégorie
          </p>
          <div className="flex gap-2">
            {/* Icon picker */}
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setShowNewIcons((p) => !p)}
                className="w-10 h-10 rounded-lg bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors ring-2 ring-primary/10"
              >
                <Icon name={newIcon} size={18} color="var(--color-primary)" />
              </button>
              {showNewIcons && (
                <div className="absolute left-0 top-12 z-50 w-56 p-2 bg-card border border-border rounded-xl shadow-xl grid grid-cols-5 gap-1">
                  {ICON_OPTIONS.map((opt) => (
                    <button
                      key={opt.name}
                      title={opt.label}
                      onClick={() => { setNewIcon(opt.name); setShowNewIcons(false); }}
                      className={`w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted transition-all ${
                        newIcon === opt.name ? 'bg-primary/10 ring-2 ring-primary/30' : ''
                      }`}
                    >
                      <Icon name={opt.name} size={16} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Name input */}
            <div className="flex-1">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => { setNewName(e.target.value); setCreateError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                  placeholder="Nom de la catégorie..."
                  className={`flex-1 px-3 py-2.5 bg-background border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                    createError ? 'border-error' : 'border-border'
                  }`}
                />
                <Button
                  variant="default"
                  iconName={creating ? 'Loader' : 'Plus'}
                  iconPosition="left"
                  disabled={creating || !newName.trim()}
                  onClick={handleCreate}
                  className="shrink-0"
                >
                  {creating ? '' : 'Créer'}
                </Button>
              </div>
              {createError && (
                <p className="text-xs text-error mt-1">{createError}</p>
              )}
              {newName && (
                <p className="text-xs text-muted-foreground mt-1 font-mono">
                  slug: {toSlug(newName)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Category list ── */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />
            ))
          ) : categories.length === 0 ? (
            <div className="text-center py-10">
              <Icon name="LayoutGrid" size={36} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Aucune catégorie. Créez-en une ci-dessus.</p>
            </div>
          ) : (
            categories.map((cat) => (
              <CategoryRow
                key={cat.id}
                cat={cat}
                onSave={handleSave}
                onDelete={handleDelete}
                isSaving={savingId === cat.id}
                isDeleting={deletingId === cat.id}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex-shrink-0 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Cliquez sur une ligne pour modifier · Glissez pour réordonner
          </p>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-sm font-medium transition-all ${
          toast.type === 'error'
            ? 'bg-error text-white'
            : 'bg-success text-white'
        }`}>
          <Icon name={toast.type === 'error' ? 'AlertCircle' : 'CheckCircle2'} size={16} />
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default CategoryManagerModal;